import DialogAssignListing from "@/components/listing/dialog-assign-listing";
import DialogUpdateListing from "@/components/listing/dialog-update-listing";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { ListingDTO } from "@shaarp/common";
import {
  CopyIcon,
  MoreHorizontalIcon,
  PencilIcon,
  Trash2Icon,
  UserPlusIcon,
} from "lucide-react";
import { MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function ListingActionsMenu({ listing }: { listing: ListingDTO }) {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const toastError = useToastError();
  const { apiFetchListings, apiFetchCollaboratorsListings } = useListings();
  const { isManager, isAdvisor, isAdmin } = useTeams();

  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogAssign, setDialogAssign] = useState(false);
  const [dialogUpdate, setDialogUpdate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const canAssign = isManager || isAdvisor || isAdmin;

  const stopPropagation = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const stopClickPropagation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const onDuplicate = async () => {
    if (isDuplicating) {
      return;
    }
    setIsDuplicating(true);
    try {
      const { data } = await api.post<ListingDTO>(
        `/listing/${listing.id}/duplicate`,
      );
      toast({
        variant: "default",
        title: t("listing.page.list.actions.duplicateSuccess.title"),
        description: t("listing.page.list.actions.duplicateSuccess.description", {
          title: listing.title,
        }),
      });
      await apiFetchListings({ force: true });
      navigate(`/listing/${data.id}`);
    } catch {
      toastError();
    } finally {
      setIsDuplicating(false);
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/listing/${listing.id}`);
      toast({
        variant: "default",
        title: t("listing.dialog.update.delete.success.title", {
          title: listing.title,
        }),
        description: t("listing.dialog.update.delete.success.description"),
      });
      await apiFetchListings({ force: true });
      setConfirmDelete(false);
    } catch {
      toastError();
    }
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="w-7 h-7 shrink-0"
            aria-label={t("listing.page.list.actions.menuLabel")}
            onClick={stopPropagation}
          >
            <MoreHorizontalIcon className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={stopPropagation}
          className="w-48"
        >
          {canAssign && (
            <>
              <DropdownMenuItem
                onSelect={() => {
                  setMenuOpen(false);
                  setDialogAssign(true);
                }}
              >
                <UserPlusIcon className="w-4 h-4 mr-2" />
                {t("listing.page.list.actions.assign")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            onSelect={() => {
              setMenuOpen(false);
              setDialogUpdate(true);
            }}
          >
            <PencilIcon className="w-4 h-4 mr-2" />
            {t("listing.page.list.actions.rename")}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isDuplicating}
            onSelect={() => {
              setMenuOpen(false);
              onDuplicate();
            }}
          >
            <CopyIcon className="w-4 h-4 mr-2" />
            {t("listing.page.list.actions.duplicate")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-500 focus:text-red-500"
            onSelect={() => {
              setMenuOpen(false);
              setConfirmDelete(true);
            }}
          >
            <Trash2Icon className="w-4 h-4 mr-2" />
            {t("listing.page.list.actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <span onClick={stopClickPropagation}>
        {dialogAssign && (
          <DialogAssignListing
            listing={listing}
            onClose={() => setDialogAssign(false)}
            onAssigned={() => {
              setDialogAssign(false);
              apiFetchListings({ force: true });
              apiFetchCollaboratorsListings({ force: true });
            }}
          />
        )}

        {dialogUpdate && (
          <DialogUpdateListing
            listing={listing}
            onClose={() => setDialogUpdate(false)}
            onUpdated={() => setDialogUpdate(false)}
            onRemoved={() => setDialogUpdate(false)}
          />
        )}

        <AlertDialog open={confirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("listing.dialog.update.delete.title", {
                  title: listing.title,
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("listing.dialog.update.delete.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmDelete(false)}>
                {t("listing.dialog.update.delete.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={onDelete}>
                {t("listing.dialog.update.delete.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </span>
    </>
  );
}
